import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import BookingPicker from '../components/BookingPicker'
import EmailProtect from '../components/EmailProtect'
import PackCard from '../components/PackCard'
import HowToGetHere from '../components/HowToGetHere'
import DoorAccessVideo from '../components/DoorAccessVideo'
import StudioHero from '../components/studio/StudioHero'
import StudioGallery from '../components/studio/StudioGallery'
import { useAuth } from '../auth/AuthProvider'
import { usePremiereStatus } from '../hooks/usePremiereStatus'
import { useLanguage } from '../i18n/LanguageProvider'
import type { MsgKey } from '../i18n/types'
import {
  COACHING_PRICE_CENTS,
  SESSION_PRICE_CENTS,
  TRIAL_PRICE_CENTS,
  formatEuros,
} from '../lib/money'

// Les deux listes portent désormais des CLÉS, plus des chaînes. Le `num` reste
// littéral : « 01 » est un chiffre de mise en page, pas du texte à traduire.
const VALUE_PROPS: ReadonlyArray<{ num: string; title: MsgKey; desc: MsgKey }> = [
  { num: '01', title: 'home.value.private.title', desc: 'home.value.private.desc' },
  { num: '02', title: 'home.value.flexible.title', desc: 'home.value.flexible.desc' },
  { num: '03', title: 'home.value.premium.title', desc: 'home.value.premium.desc' },
]

const HOW_IT_WORKS: ReadonlyArray<{ num: string; title: MsgKey; desc: MsgKey }> = [
  { num: '01', title: 'home.how.pick.title', desc: 'home.how.pick.desc' },
  { num: '02', title: 'home.how.pay.title', desc: 'home.how.pay.desc' },
  { num: '03', title: 'home.how.pin.title', desc: 'home.how.pin.desc' },
]

export default function Home() {
  const { user } = useAuth()
  const { lang, t } = useLanguage()
  // Première séance hero CTA (operator 2026-06-26): shown next to the 25€ CTA at
  // the top of the site. Anon visitors always see it (acquisition → /login). A
  // logged-in visitor who already consumed their trial does NOT (hide once used).
  const { loading: premiereLoading, state: premiereState } = usePremiereStatus(user?.id)
  const showPremiereHero = !(user && !premiereLoading && premiereState === 'consumed')
  const [calLink, setCalLink] = useState('studiocoaching')
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCancelToast, setShowCancelToast] = useState(
    searchParams.get('checkout') === 'cancelled',
  )

  const sessionPrice = formatEuros(SESSION_PRICE_CENTS, lang)
  const trialPrice = formatEuros(TRIAL_PRICE_CENTS, lang)
  const coachingPrice = formatEuros(COACHING_PRICE_CENTS, lang)

  // Strip ?checkout=cancelled from URL once we've captured it into local state
  useEffect(() => {
    if (searchParams.get('checkout') === 'cancelled') {
      const next = new URLSearchParams(searchParams)
      next.delete('checkout')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-dismiss cancel toast after 8s
  useEffect(() => {
    if (!showCancelToast) return
    const t = setTimeout(() => setShowCancelToast(false), 8000)
    return () => clearTimeout(t)
  }, [showCancelToast])

  function handleBooking(link: string) {
    setCalLink(link)
    document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <Header />
      {showCancelToast && (
        <div
          role="status"
          aria-live="polite"
          className="max-w-[520px] mx-auto mt-28 mb-4 px-6"
        >
          <div className="bg-surface border border-border border-l-2 border-l-text-muted rounded-[4px] p-4 flex items-start gap-3">
            <p className="text-text text-sm flex-1">{t('home.cancel.message')}</p>
            <button
              type="button"
              aria-label={t('home.cancel.close')}
              onClick={() => setShowCancelToast(false)}
              className="text-text-muted hover:text-text text-lg leading-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-[4px] px-2"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <main>
        {/* Hero / Concept */}
        <section id="concept" className="pt-28 pb-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <div className="mb-12">
              <StudioHero />
            </div>
            <div className="mb-16">
              <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
                {t('home.hero.eyebrow')}
              </span>
              <h1 className="font-display font-[800] text-[clamp(2.4rem,6vw,4rem)] leading-[1.05] tracking-[-0.02em] mb-6">
                {t('home.hero.titleLead')} <span className="text-accent">{t('home.hero.titleAccent')}</span> {t('home.hero.titleTail')}
              </h1>
              <p className="text-text-muted text-lg max-w-2xl">
                {t('home.hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <button
                  onClick={() => handleBooking('studiocoaching/seance-prive')}
                  className="bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer"
                >
                  {t('home.hero.ctaSession', { price: sessionPrice })}
                </button>
                {showPremiereHero && (
                  <Link
                    to={user ? '/reserve/trial' : '/login'}
                    data-testid="hero-premiere-cta"
                    className="border border-accent text-accent hover:bg-accent hover:text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px inline-flex items-center"
                  >
                    {t('home.hero.ctaTrial', { price: trialPrice })}
                  </Link>
                )}
              </div>
            </div>

            <h2 className="sr-only">{t('home.value.srTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {VALUE_PROPS.map(({ num, title, desc }) => (
                <div
                  key={num}
                  className="bg-surface border border-border rounded-[8px] p-6 transition-all duration-200 hover:border-accent motion-safe:hover:-translate-y-0.5"
                >
                  <span className="text-accent font-display font-[800] text-sm">{num}</span>
                  <h3 className="font-display font-bold text-lg mt-2 mb-2">{t(title)}</h3>
                  <p className="text-text-muted text-base leading-relaxed">{t(desc)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section id="how-it-works" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.how.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-8">
              {t('home.how.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {HOW_IT_WORKS.map(({ num, title, desc }) => (
                <div
                  key={num}
                  className="bg-surface border border-border rounded-[8px] p-6 transition-all duration-200 hover:border-accent motion-safe:hover:-translate-y-0.5"
                >
                  <span className="text-accent font-display font-[800] text-sm">{num}</span>
                  <h3 className="font-display font-bold text-lg mt-2 mb-2">{t(title)}</h3>
                  <p className="text-text-muted text-base leading-relaxed">
                    {t(desc, { price: sessionPrice })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reservation */}
        <section id="reservation" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.booking.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-4">
              {t('home.booking.title')}
            </h2>
            <p className="text-text-muted mb-8">{t('home.booking.lead')}</p>
            <BookingPicker calLink={calLink} eventTypeSlug="studiocoaching" />
          </div>
        </section>

        {/* Le studio en images (Phase 53 VIS-03 — consolidated single gallery) */}
        <section id="gallery" aria-labelledby="gallery-title" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.gallery.eyebrow')}
            </span>
            <h2 id="gallery-title" className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-4">
              {t('home.gallery.title')}
            </h2>
            <p className="text-text-muted mb-8 max-w-2xl">{t('home.gallery.lead')}</p>
            <StudioGallery />
          </div>
        </section>

        {/* Tarifs */}
        <section id="tarifs" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.pricing.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-4">
              {t('home.pricing.title')}
            </h2>
            <p className="text-text-muted mb-8">{t('home.pricing.lead')}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 max-w-5xl gap-6">
              {/* Première séance — acquisition hook (Phase 71-06 TRIAL-06).
                  Always visible on the public vitrine. CTA target is
                  auth-conditional: logged-out → account creation (/login),
                  logged-in → the première booking page (/reserve/trial). */}
              <div className="bg-surface border border-accent rounded-[8px] p-8 transition-all duration-200 motion-safe:hover:-translate-y-0.5 flex flex-col">
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-accent mb-3 block">
                  {t('home.pricing.trial.eyebrow')}
                </span>
                <h3 className="font-display font-bold text-xl mb-2">
                  {t('home.pricing.trial.title')}
                </h3>
                <p className="text-text-muted text-base mb-6">{t('home.pricing.trial.desc')}</p>
                <div className="mb-6">
                  <span className="font-display font-[800] text-3xl">{trialPrice}</span>
                  <span className="text-text-muted ml-1">{t('home.pricing.trial.unit')}</span>
                </div>
                <div className="flex flex-col gap-3 mt-auto">
                  {user ? (
                    <Link
                      to="/reserve/trial"
                      className="w-full bg-accent hover:bg-accent-hover text-[#1a1209] font-medium py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer text-center"
                    >
                      {t('home.pricing.trial.ctaBook')}
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      className="w-full bg-accent hover:bg-accent-hover text-[#1a1209] font-medium py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer text-center"
                    >
                      {t('home.pricing.trial.ctaSignup')}
                    </Link>
                  )}
                </div>
              </div>

              <div className="bg-surface border border-border hover:border-accent rounded-[8px] p-8 transition-all duration-200 motion-safe:hover:-translate-y-0.5 flex flex-col">
                <h3 className="font-display font-bold text-xl mb-2">
                  {t('home.pricing.session.title')}
                </h3>
                <p className="text-text-muted text-base mb-6">{t('home.pricing.session.desc')}</p>
                <div className="mb-6">
                  <span className="font-display font-[800] text-3xl">{sessionPrice}</span>
                  <span className="text-text-muted ml-1">{t('home.pricing.session.unit')}</span>
                </div>
                <p className="text-text-muted text-sm mb-6">
                  {t('home.pricing.session.packHint')}
                </p>
                <div className="flex flex-col gap-3 mt-auto">
                  <Link
                    to="/session/checkout"
                    className="w-full bg-accent hover:bg-accent-hover text-[#1a1209] font-medium py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer text-center"
                  >
                    {t('home.pricing.session.cta')}
                  </Link>
                </div>
              </div>

              {/* Coaching personnalisé — Phase 74-03 (COACH-01). 3rd card, 80€.
                  Séance avec le coach, à réserver ≥48h à l'avance. CTA target is
                  auth-conditional: logged-out → account creation (/login),
                  logged-in → the coaching booking page (/reserve/coaching-perso).
                  No once-per-user — coaching repeats. */}
              <div className="bg-surface border border-border hover:border-accent rounded-[8px] p-8 transition-all duration-200 motion-safe:hover:-translate-y-0.5 flex flex-col">
                <h3 className="font-display font-bold text-xl mb-2">
                  {t('home.pricing.coaching.title')}
                </h3>
                <p className="text-text-muted text-base mb-6">{t('home.pricing.coaching.desc')}</p>
                <div className="mb-6">
                  <span className="font-display font-[800] text-3xl">{coachingPrice}</span>
                  <span className="text-text-muted ml-1">{t('home.pricing.coaching.unit')}</span>
                </div>
                <div className="flex flex-col gap-3 mt-auto">
                  <Link
                    to={user ? '/reserve/coaching-perso' : '/login'}
                    data-testid="coaching-perso-cta"
                    className="w-full bg-accent hover:bg-accent-hover text-[#1a1209] font-medium py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer text-center"
                  >
                    {t('home.pricing.coaching.cta')}
                  </Link>
                </div>
              </div>
            </div>

            <p className="text-text-muted text-sm mt-8 text-center">
              {t('home.pricing.footnote')}
            </p>
          </div>
        </section>

        {/* Packs (Phase 2) */}
        <section id="packs" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.packs.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-4">
              {t('home.packs.title')}
            </h2>
            <p className="text-text-muted mb-8">{t('home.packs.lead')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <PackCard size={10} origin="home" />
              <PackCard size={30} origin="home" />
              <PackCard size={50} origin="home" />
            </div>
          </div>
        </section>

        {/* Accès au studio — vidéo explicative ouverture/fermeture porte (Phase 76) */}
        <section id="acces-porte" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.door.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-4">
              {t('home.door.title')}
            </h2>
            <p className="text-text-muted mb-8 max-w-2xl">{t('home.door.lead')}</p>
            <DoorAccessVideo />
          </div>
        </section>

        {/* Comment venir */}
        <HowToGetHere />

        {/* Contact */}
        <section id="contact" className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
              {t('home.contact.eyebrow')}
            </span>
            <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-6">
              {t('home.contact.title')}
            </h2>
            <div className="space-y-2 mb-8">
              <p>{t('home.contact.emailLabel')}<EmailProtect user="contact" domain="sweatstudio.fr" /></p>
              <p>{t('home.contact.address')}</p>
            </div>
            <button
              onClick={() => handleBooking('studiocoaching')}
              className="bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px cursor-pointer"
            >
              {t('home.contact.cta')}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
